import React, { useState, useEffect, useCallback } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { clientService } from "@/services/clientService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ClientInputProps {
  value: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  placeholder?: string;
  onChange?: (clientId: string | null) => void;
}

export function ClientInput({
  value,
  state = "editing",
  label = "Client",
  error,
  placeholder = "Select client…",
  onChange,
}: ClientInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleIdlePress = useCallback(() => {
    if (!value) return;
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
    if (currentPanel && currentId) {
      pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
    }
    navigateToEntity("client", value);
  }, [value, currentParams, pushNavigation, currentEntity, navigateToEntity]);
  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const clients = await clientService.getClients();
        if (cancelled) return;
        setOptions(
          clients.map((c) => ({
            id: c.id!,
            name: c.name,
            subtitle: c.description ?? undefined,
            icon: "office-building" as const,
            badge: {
              text: c.status,
              tone: c.status === "ACTIVE" ? ("emerald" as const) : ("amber" as const),
            },
          })),
        );
      } catch (err) {
        console.error("Failed to load clients:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="domain"
      error={error}
      placeholder={placeholder}
      onChange={onChange}
      onIdlePress={handleIdlePress}
    />
  );
}
